import { CustomHeader } from "@/components/CustomHeader";
import { GroupAvatar } from "@/components/GroupAvatar.web";
import { ImagePreviewModal } from "@/components/ImagePreviewModal.web";
import { PhoneVerifyBanner } from "@/components/PhoneVerifyBanner";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import AlertModal from "@/components/ui/AlertModal.web";
import { Button } from "@/components/ui/Button";
import { Fab } from "@/components/ui/Fab.web";
import { useAuth } from "@/context/AuthContext";
import { firestore } from "@/firebase/web";
import {
  arrayUnion,
  deleteDoc,
  doc,
  collection as firestoreCollection,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";

import { Group } from "@/types/group";
import { Invitation } from "@/types/invitation";
import {
  Link,
  Stack,
  useLocalSearchParams,
  useRouter,
} from "expo-router"; // Changed useRoute to useLocalSearchParams
import Head from "expo-router/head";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  FlatList,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { getGroupPictureOriginal } from "../../../utils/getGroupPictureOriginal";
import { timestampToMillis } from "../../../utils/timestampToMillis";
const DefaultGroupImage = require("../../../assets/images/default-group.webp");

// Shape rendered by the groups FlatList. Member groups are full `Group`
// docs; pending entries only carry id+name (from the access request doc)
// plus the `pending` flag — the renderer reads `description` /
// `photoUpdatedAt` only when present and falls back to the default group
// avatar otherwise.
type GroupListItem = {
  id: string;
  name: string;
  description?: string;
  photoUpdatedAt?: Group["photoUpdatedAt"];
  pending: boolean;
  hasUnread: boolean;
};

type PreviewItem = {
  id: string;
  photoUpdatedAt?: Group["photoUpdatedAt"];
};

type GroupRowProps = {
  item: GroupListItem;
  fallbackOverride?: string;
  pendingLabel: string;
  onOpenPreview: (item: PreviewItem) => void;
};

const GroupRowInner: React.FC<GroupRowProps> = ({
  item,
  fallbackOverride,
  pendingLabel,
  onOpenPreview,
}) => (
  <Link href={`/groups/${item.id}`} asChild>
    <TouchableOpacity style={styles.groupItem}>
      <TouchableOpacity
        style={styles.avatarTouchable}
        onPress={(e) => {
          e.preventDefault?.();
          e.stopPropagation?.();
          onOpenPreview({ id: item.id, photoUpdatedAt: item.photoUpdatedAt });
        }}
      >
        <GroupAvatar
          groupId={item.id}
          photoUpdatedAt={item.photoUpdatedAt}
          fallbackOverride={fallbackOverride}
        />
      </TouchableOpacity>
      <ThemedView style={styles.groupItemTextContainer}>
        <ThemedText type="subtitle" numberOfLines={1} ellipsizeMode="tail">
          {item.name}
        </ThemedText>
        {item.pending ? (
          <ThemedText style={styles.pendingBadge}>{pendingLabel}</ThemedText>
        ) : (
          <ThemedText
            style={styles.description}
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            {item.description}
          </ThemedText>
        )}
      </ThemedView>
      {item.hasUnread && !item.pending ? (
        <View
          style={styles.unreadDot}
          testID={`group-unread-dot-${item.id}`}
        />
      ) : null}
    </TouchableOpacity>
  </Link>
);

// `displayItems` re-runs on every groups/pendingRequests snapshot, producing
// fresh item objects. Compare on individual fields so unrelated re-renders
// (e.g. `busyInvitationId` toggling during accept/decline) don't force every
// row to re-render.
const GroupRow = memo(GroupRowInner, (prev, next) => {
  return (
    prev.item.id === next.item.id &&
    prev.item.name === next.item.name &&
    prev.item.description === next.item.description &&
    prev.item.pending === next.item.pending &&
    prev.item.photoUpdatedAt === next.item.photoUpdatedAt &&
    prev.item.hasUnread === next.item.hasUnread &&
    prev.fallbackOverride === next.fallbackOverride &&
    prev.pendingLabel === next.pendingLabel &&
    prev.onOpenPreview === next.onOpenPreview
  );
});

export default function GroupsScreen() {
  const { user, userData } = useAuth();
  const userUid = user?.uid;
  const lastViewedMap = userData?.groupsLastViewedAt;
  const router = useRouter();
  const params = useLocalSearchParams() as {
    newGroupImage?: string;
    newGroupId?: string;
  }; // Use useLocalSearchParams for web
  const [groups, setGroups] = useState<Group[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [pendingRequests, setPendingRequests] = useState<
    { id: string; groupId: string; groupName: string }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [previewItem, setPreviewItem] = useState<PreviewItem | null>(null);
  const [busyInvitationId, setBusyInvitationId] = useState<string | null>(null);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const { t } = useTranslation();

  const fallbackLoadGroups = useCallback(async (uid: string) => {
    try {
      const userSnap = await getDoc(doc(firestore, "users", uid));
      const groupIds = userSnap.data()?.groupIds;
      if (!Array.isArray(groupIds) || groupIds.length === 0) {
        setGroups([]);
        setLoading(false);
        return;
      }

      const results = await Promise.all(
        groupIds.map((groupId) =>
          getDoc(doc(firestore, "groups", groupId))
            .then((snap) => {
              if (!snap.exists()) return null;
              const data = snap.data();
              if (
                Array.isArray(data?.memberIds) &&
                data.memberIds.includes(uid)
              ) {
                return { id: snap.id, ...data } as Group;
              }
              return null;
            })
            .catch(() => null),
        ),
      );

      setGroups(results.filter(Boolean) as Group[]);
    } catch (err) {
      // swallow errors in fallback
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!userUid) return;

    const groupsQuery = query(
      firestoreCollection(firestore, "groups"),
      where("memberIds", "array-contains", userUid),
    );
    const unsubscribeGroups = onSnapshot(
      groupsQuery,
      (snapshot) => {
        const groupsData = snapshot.docs.map(
          (doc) => ({ id: doc.id, ...doc.data() }) as Group,
        );
        setGroups(groupsData);
        setLoading(false);
      },
      (error) => {
        const errorCode = (error as { code?: string })?.code;
        const isPermissionDenied =
          errorCode === "permission-denied" ||
          errorCode === "firestore/permission-denied";
        if (isPermissionDenied) {
          console.warn("Groups listener permission denied by Firestore rules.");
        } else {
          console.error("Groups listener error:", error);
        }
        fallbackLoadGroups(userUid);
      },
    );

    // Invitations are keyed on inviteeUid so phone-only users (email === null)
    // receive them too.
    const invitationsQuery = query(
      firestoreCollection(firestore, "groupInvitations"),
      where("inviteeUid", "==", userUid),
      where("status", "==", "pending"),
    );
    const unsubscribeInvites = onSnapshot(
      invitationsQuery,
      (snapshot) => {
        const invitesData = snapshot.docs.map(
          (doc) => ({ id: doc.id, ...doc.data() }) as Invitation,
        );
        setInvitations(invitesData);
      },
      (error) => {
        const errorCode = (error as { code?: string })?.code;
        const isPermissionDenied =
          errorCode === "permission-denied" ||
          errorCode === "firestore/permission-denied";
        if (isPermissionDenied) {
          console.warn(
            "Group invitations listener permission denied by Firestore rules.",
          );
        } else {
          console.error("Group invitations listener error:", error);
        }
        setInvitations([]);
      },
    );

    // Pending join requests this user has filed. Surfaces both the new
    // signup-URL `groupId` flow (utils/processGroupIdParam.web.ts) and the
    // pre-existing manual "Request access" click on a group URL
    // (components/RequestGroupAccess.web.tsx). Same query shape as
    // hooks/useGroupAccess.web.ts lines 64-69. The access request doc
    // already carries groupId + groupName, so no extra reads against the
    // groups collection are needed for the list view.
    const pendingRequestsQuery = query(
      firestoreCollection(firestore, "groupAccessRequests"),
      where("requesterId", "==", userUid),
      where("status", "==", "pending"),
    );
    const unsubscribePendingRequests = onSnapshot(
      pendingRequestsQuery,
      (snapshot) => {
        setPendingRequests(
          snapshot.docs.map((d) => {
            const data = d.data();
            return {
              id: d.id,
              groupId: data.groupId as string,
              groupName: (data.groupName as string) ?? "",
            };
          }),
        );
      },
      (error) => {
        const errorCode = (error as { code?: string })?.code;
        const isPermissionDenied =
          errorCode === "permission-denied" ||
          errorCode === "firestore/permission-denied";
        if (isPermissionDenied) {
          console.warn(
            "Pending requests listener permission denied by Firestore rules.",
          );
        } else {
          console.error("Pending requests listener error:", error);
        }
        setPendingRequests([]);
      },
    );

    return () => {
      unsubscribeGroups();
      unsubscribeInvites();
      unsubscribePendingRequests();
    };
  }, [userUid, fallbackLoadGroups]);

  // Combine member groups with pending join requests for the list view.
  // Dedupe: if a groupId appears in both (stale request that wasn't cleared
  // after approval, or a race between the two snapshots), prefer the
  // member render — the user IS in the group, the pending badge would be
  // misleading.
  const displayItems = useMemo<GroupListItem[]>(() => {
    const memberSet = new Set(groups.map((g) => g.id));
    const pendingItems: GroupListItem[] = pendingRequests
      .filter((p) => !memberSet.has(p.groupId))
      .map((p) => ({
        id: p.groupId,
        name: p.groupName,
        pending: true,
        hasUnread: false,
      }));
    // Sort by lastPostAt desc so groups with the most recent activity float
    // to the top. Groups without lastPostAt sort to the bottom (missing
    // → 0). Array.prototype.sort is stable, so ties keep Firestore's order.
    const sortedGroups = [...groups].sort(
      (a, b) => timestampToMillis(b.lastPostAt) - timestampToMillis(a.lastPostAt),
    );
    const memberItems: GroupListItem[] = sortedGroups.map((g) => {
      const lastPostMs = timestampToMillis(g.lastPostAt);
      const lastViewedMs = timestampToMillis(lastViewedMap?.[g.id]);
      return {
        id: g.id,
        name: g.name,
        description: g.description,
        photoUpdatedAt: g.photoUpdatedAt,
        pending: false,
        hasUnread: lastPostMs > 0 && lastPostMs > lastViewedMs,
      };
    });
    return [...memberItems, ...pendingItems];
  }, [groups, pendingRequests, lastViewedMap]);

  // Look up the matching GROUP_INVITATION notification (if any) and stamp
  // interactionStatus on it so the notifications tab reflects the choice.
  // Best-effort — errors are logged but don't fail the whole interaction.
  const markInvitationNotification = async (
    uid: string,
    invitationId: string,
    status: "accepted" | "declined",
  ) => {
    try {
      const notifQuery = query(
        firestoreCollection(firestore, "users", uid, "notifications"),
        where("resourceId", "==", invitationId),
        where("type", "==", "GROUP_INVITATION"),
        limit(1),
      );
      const snap = await getDocs(notifQuery);
      if (snap.empty) return;
      await updateDoc(snap.docs[0].ref, {
        read: true,
        interactionStatus: status,
      });
    } catch (error) {
      console.warn(
        "Failed to stamp invitation notification interactionStatus:",
        error,
      );
    }
  };

  const handleAccept = async (invitationId: string, groupId: string) => {
    if (!userUid || busyInvitationId) return;
    setBusyInvitationId(invitationId);
    try {
      try {
        const batch = writeBatch(firestore);
        batch.update(doc(firestore, "users", userUid), {
          groupIds: arrayUnion(groupId),
        });
        batch.update(doc(firestore, "groups", groupId), {
          memberIds: arrayUnion(userUid),
        });
        batch.delete(doc(firestore, "groupInvitations", invitationId));
        await batch.commit();
      } catch (batchError) {
        // Legacy invitations that were resolved via the old status-flip flow
        // can't be deleted (rule requires status == "pending"). Log and still
        // mark the notification so the row reflects the choice.
        console.warn(
          "Invitation batch failed (likely already resolved):",
          batchError,
        );
      }
      await markInvitationNotification(userUid, invitationId, "accepted");
    } catch (error) {
      console.error("Error accepting invitation:", error);
      setAlertMessage(t("INVITATION_ACCEPT_FAILED"));
    } finally {
      setBusyInvitationId(null);
    }
  };

  const handleDecline = async (invitationId: string) => {
    if (!userUid || busyInvitationId) return;
    setBusyInvitationId(invitationId);
    try {
      try {
        await deleteDoc(doc(firestore, "groupInvitations", invitationId));
      } catch (deleteError) {
        console.warn(
          "Invitation delete failed (likely already resolved):",
          deleteError,
        );
      }
      await markInvitationNotification(userUid, invitationId, "declined");
    } catch (error) {
      console.error("Error declining invitation:", error);
      setAlertMessage(t("INVITATION_DECLINE_FAILED"));
    } finally {
      setBusyInvitationId(null);
    }
  };

  const handleOpenPreview = useCallback(
    (item: PreviewItem) => setPreviewItem(item),
    [],
  );

  const pendingLabel = t("REQUEST_UNDER_REVIEW");
  const newGroupImage = params.newGroupImage;
  const newGroupId = params.newGroupId;

  const keyExtractor = useCallback((item: GroupListItem) => item.id, []);

  const renderItem = useCallback(
    ({ item }: { item: GroupListItem }) => (
      <GroupRow
        item={item}
        pendingLabel={pendingLabel}
        fallbackOverride={
          newGroupImage && newGroupId === item.id
            ? (newGroupImage as string)
            : undefined
        }
        onOpenPreview={handleOpenPreview}
      />
    ),
    [pendingLabel, newGroupImage, newGroupId, handleOpenPreview],
  );

  // Invitations render as the list header so the outer view no longer nests
  // one FlatList inside another. Small N (usually 0–1); a plain map is fine
  // and avoids the virtualisation-in-virtualisation warning.
  const invitationsHeader = useMemo(() => {
    if (invitations.length === 0) return null;
    return (
      <ThemedView style={styles.invitationsContainer}>
        <ThemedText type="subtitle">{t("INVITATIONS")}</ThemedText>
        {invitations.map((item) => (
          <ThemedView key={item.id} style={styles.invitationItem}>
            <ThemedText>
              {t("JOIN")}{" "}
              <ThemedText type="defaultSemiBold">
                {item.groupName || "a group"}
              </ThemedText>
            </ThemedText>
            <ThemedView style={styles.invitationActions}>
              <Button
                text={
                  busyInvitationId === item.id ? undefined : t("ACCEPT")
                }
                icon={
                  busyInvitationId === item.id ? (
                    <ActivityIndicator size="small" color="#fff" />
                  ) : undefined
                }
                onPress={() => handleAccept(item.id, item.groupId)}
                disabled={!!busyInvitationId}
                testID={`invitation-accept-${item.id}`}
                style={{ flex: 1 }}
              />
              <Button
                text={t("DECLINE")}
                onPress={() => handleDecline(item.id)}
                disabled={!!busyInvitationId}
                testID={`invitation-decline-${item.id}`}
                style={{ marginLeft: 10, backgroundColor: "red", flex: 1 }}
              />
            </ThemedView>
          </ThemedView>
        ))}
      </ThemedView>
    );
    // handleAccept / handleDecline are closures over `busyInvitationId`, so
    // pinning that dep here also invalidates the header when they flip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitations, busyInvitationId, t]);

  return (
    <ThemedView style={styles.container}>
      <Head>
        <title>{t("PAGE_TITLE_GROUPS")}</title>
      </Head>
      <Stack.Screen options={{ headerShown: false }} />
      <CustomHeader title={t("GROUPS")} type="subtitle" variant="tab" />
      <PhoneVerifyBanner />
      {loading ? (
        <>
          {invitationsHeader}
          <ThemedView style={styles.centered}>
            <ActivityIndicator />
          </ThemedView>
        </>
      ) : (
        <FlatList
          data={displayItems}
          contentContainerStyle={
            displayItems.length === 0
              ? styles.emptyListContentContainer
              : undefined
          }
          keyExtractor={keyExtractor}
          renderItem={renderItem}
          ListHeaderComponent={invitationsHeader}
          initialNumToRender={10}
          maxToRenderPerBatch={10}
          windowSize={11}
          removeClippedSubviews
          ListEmptyComponent={() => (
            <ThemedView style={styles.centered}>
              <ThemedText style={styles.emptyText}>
                {t("NO_GROUPS_FOUND")}
              </ThemedText>
              <ThemedText style={styles.emptyText}>
                {t("CREATE_A_NEW_ONE")}
              </ThemedText>
            </ThemedView>
          )}
        />
      )}
      <Fab
        onPress={() => router.push("/(tabs)/groups/create")}
        accessibilityLabel={t("CREATE_GROUP")}
        testID="fab-create-group"
      />
      <ImagePreviewModal
        visible={!!previewItem}
        onClose={() => setPreviewItem(null)}
        uri={
          previewItem
            ? getGroupPictureOriginal(
                previewItem.id,
                previewItem.photoUpdatedAt?.toMillis?.(),
              )
            : undefined
        }
        fallback={DefaultGroupImage}
      />
      <AlertModal
        visible={!!alertMessage}
        title={t("ERROR")}
        message={alertMessage ?? ""}
        onClose={() => setAlertMessage(null)}
        primaryAction={{
          text: t("OK"),
          onPress: () => setAlertMessage(null),
        }}
      />
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  groupItem: {
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    flexDirection: "row",
  },
  avatarTouchable: {
    marginRight: 12,
  },
  description: {
    fontSize: 14,
    color: "#666",
  },
  pendingBadge: {
    fontSize: 13,
    color: "#1e3a8a",
    fontStyle: "italic",
    marginTop: 2,
  },
  groupItemTextContainer: {
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
  },
  unreadDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: "#3b82f6",
    alignSelf: "center",
    marginLeft: 8,
  },
  invitationsContainer: {
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
  },
  invitationItem: {
    paddingVertical: 10,
  },
  invitationActions: {
    flexDirection: "row",
    marginTop: 10,
  },
  emptyListContentContainer: {
    flexGrow: 1,
  },
  emptyText: {
    color: "#666",
  },
});
